import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, Flag, EyeOff } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Spinner, EmptyState } from "@/components/shared";
import { toast } from "sonner";

const DeleteBtn = ({ onClick, testId }) => (
  <Button size="sm" variant="outline" onClick={onClick} data-testid={testId} className="rounded-full text-red-600 border-red-200 hover:bg-red-50 shrink-0"><Trash2 size={14} className="mr-1" /> Delete</Button>
);

export const AdminCommunity = () => {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/community").then((r) => setData(r.data)).catch(() => setData({ posts: [], comments: [] }));
  useEffect(() => { load(); }, []);

  const remove = async (kind, id, msg) => {
    if (!window.confirm(msg)) return;
    try { await api.delete(`/admin/community/${kind}/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const dismiss = async (id) => {
    try { await api.post(`/admin/community/comments/${id}/dismiss`); toast.success("Reports dismissed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!data) return <Spinner />;
  const flagged = data.comments.filter((c) => c.reports.length).length;
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <section data-testid="admin-community-comments">
        <h3 className="font-display font-bold text-lg text-[#0B1733] mb-3">Recent comments ({data.comments.length})
          {flagged > 0 && <span className="ml-2 align-middle text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-600" data-testid="flagged-count">{flagged} flagged</span>}
        </h3>
        {data.comments.length === 0 ? <EmptyState title="No comments yet" /> : (
          <div className="bg-white rounded-2xl border border-[#E3E8E8] overflow-hidden">
            {data.comments.map((c) => (
              <div key={c.id} className={`p-4 border-b border-[#E3E8E8] last:border-0 flex items-start justify-between gap-3 ${c.reports.length ? "bg-red-50/60" : ""}`} data-testid={`admin-comment-${c.id}`}>
                <div className="min-w-0">
                  {c.reports.length > 0 && <div className="flex flex-wrap items-center gap-2 mb-1">
                    {<span className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-600" data-testid={`flagged-badge-${c.id}`}><Flag size={12} /> Flagged by {c.reports.length} {c.reports.length === 1 ? "member" : "members"}</span>}
                    {c.hidden && <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#0B1733] text-white" data-testid={`hidden-badge-${c.id}`}><EyeOff size={11} /> Hidden from visitors</span>}
                  </div>}
                  <p className="text-xs text-[#4A5568]"><b className="text-[#0B1733]">{c.author}</b> on <Link to={`/community/posts/${c.post_id}`} className="text-[#007F83] hover:underline">{c.post_title}</Link></p>
                  <p className="text-sm text-[#4A5568] mt-1 whitespace-pre-wrap break-words">{c.body}</p>
                  {c.reports.filter((r) => r.reason).map((r, i) => <p key={i} className="text-xs text-red-700 mt-1">“{r.reason}” — {r.by}</p>)}
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <DeleteBtn testId={`admin-delete-comment-${c.id}`} onClick={() => remove("comments", c.id, "Delete this comment?")} />
                  {c.reports.length > 0 && <Button size="sm" variant="outline" onClick={() => dismiss(c.id)} data-testid={`admin-dismiss-${c.id}`} className="rounded-full">{c.hidden ? "Keep & show" : "Keep"}</Button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
      <section data-testid="admin-community-posts">
        <h3 className="font-display font-bold text-lg text-[#0B1733] mb-3">Posts ({data.posts.length})</h3>
        {data.posts.length === 0 ? <EmptyState title="No posts yet" /> : (
          <div className="bg-white rounded-2xl border border-[#E3E8E8] overflow-hidden">
            {data.posts.map((p) => (
              <div key={p.id} className="p-4 border-b border-[#E3E8E8] last:border-0 flex items-start justify-between gap-3" data-testid={`admin-post-${p.id}`}>
                <div className="min-w-0">
                  <Link to={`/community/posts/${p.id}`} className="font-semibold text-[#0B1733] hover:text-[#007F83]">{p.title}</Link>
                  <p className="text-xs text-[#4A5568] mt-1">by {p.author} · {p.reply_count} replies</p>
                </div>
                <DeleteBtn testId={`admin-delete-post-${p.id}`} onClick={() => remove("posts", p.id, "Delete this post and all its comments?")} />
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};
